import { useEffect, useRef } from 'react';

import { changeLocale, currentLocale, localeDefinition, SUPPORTED_LOCALES } from '@/shared/i18n';

import { savePreferences } from './api/preferences';

/**
 * The language the user picked in this tab while its profile save may still be in flight. Session refetches that
 * still carry an older profile value (e.g. the save of a previous pick finishing late) must not undo it.
 */
let pendingChoice: string | null = null;

/** A language picked before sign-in (login page). Saved to the profile after sign-in only if the profile has none. */
let signedOutChoice: string | null = null;

/** The user picked a language: switch now (no sign-out), remember it locally and on the profile. */
export async function selectLocale(code: string) {
  await changeLocale(code);
  // Pseudo locales are a development tool, never a profile setting.
  if (localeDefinition(code).pseudo) return;
  saveProfileLocale(code);
}

function saveProfileLocale(code: string) {
  pendingChoice = code;
  void savePreferences({ locale: code }).catch(() => {
    if (pendingChoice === code) pendingChoice = null;
  });
}

/** Before sign-in there is no profile: switch now and remember the choice in this browser only. */
export async function selectLocaleSignedOut(code: string) {
  await changeLocale(code);
  signedOutChoice = localeDefinition(code).pseudo ? null : code;
}

/**
 * Once the session loads, the profile's language (when set) is authoritative over the browser's stored choice.
 * Runs on sign-in and whenever the server preference changes.
 */
export function useProfileLocale(profileLocale: string | undefined) {
  // React to the profile value changing (sign-in, a change made on another device), not to every render where it
  // differs from the screen: right after the user switches language, a session refetch may still carry the old value.
  const applied = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!profileLocale) {
      // No saved profile language: keep the screen as it is, and save a choice made on the sign-in page.
      const choice = signedOutChoice;
      signedOutChoice = null;
      if (choice && choice === currentLocale()) saveProfileLocale(choice);
      return;
    }
    // A profile language exists: it wins over a sign-in page choice.
    signedOutChoice = null;
    if (profileLocale === applied.current) return;
    if (pendingChoice !== null) {
      // The profile has caught up with the user's latest pick: settle. Any other value is a stale refetch.
      if (profileLocale === pendingChoice) pendingChoice = null;
      applied.current = profileLocale;
      return;
    }
    applied.current = profileLocale;
    if (!SUPPORTED_LOCALES.some((l) => l.code === profileLocale)) return;
    if (profileLocale !== currentLocale()) void changeLocale(profileLocale);
  }, [profileLocale]);
}
